import { format, isToday } from "date-fns";
import { useMemo, useState } from "react";
import { Button, cn } from "web-design-system";
import {
  CalendarIcon,
  HistoryIcon,
  PlusIcon,
  StickyNoteIcon,
} from "lucide-react";
import { getFirstName } from "creso-core/utils";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { useEvents, useFetchNote, usePendingEvents } from "creso-core/hooks";
import { handleError } from "@/utils/error";
import { NoteTask } from "creso-core/types";
import { NoteTaskItem } from "./note-task-item";
import { TaskEditor } from "./task-editor";
import { useNotebookContext } from "./notebook-context";
import { transformNoteDetailToListItem } from "./utils";
import {
  useFamilyMemberEvents,
  useFamilyMemberPendingEvents,
} from "./use-family-member-events";

export function TaskSection({
  initialIsAdding = false,
}: {
  initialIsAdding?: boolean;
}) {
  const { filters, clientIds } = useNotebookContext();
  const isFamilyMode = Boolean(clientIds?.length);
  const selectedDate = format(filters.date, "yyyy-MM-dd");
  const showFamilyTasks = isFamilyMode && !filters.client;

  const { data: clientEvents } = useEvents(
    {
      clientId: filters.client?.id,
      date: selectedDate,
    },
    { enabled: !showFamilyTasks }
  );

  const { data: familyEvents } = useFamilyMemberEvents(
    showFamilyTasks ? (clientIds ?? []) : [],
    selectedDate
  );

  const { data: clientPendingTasks } = usePendingEvents(
    { clientId: filters.client?.id },
    { enabled: !showFamilyTasks }
  );

  const { data: familyPendingTasks } = useFamilyMemberPendingEvents(
    showFamilyTasks ? (clientIds ?? []) : []
  );

  const data = showFamilyTasks ? familyEvents : clientEvents;
  const pendingTasks = showFamilyTasks
    ? familyPendingTasks
    : clientPendingTasks;

  const tasks = useMemo(() => {
    const today = isToday(filters.date);
    return data
      ?.filter((d) => d.type === "NOTE" || d.type === "TASK")
      ?.filter((d) => (d.date ? true : today))
      .sort((a, b) => (a.date ? 0 : 1) - (b.date ? 0 : 1));
  }, [data, filters.date]);

  const [isAddingTask, setIsAddingTask] = useState(initialIsAdding);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);

  return (
    <div className="flex-1 flex flex-col bg-background border border-border px-6 py-3 pb-6 gap-4 rounded-lg overflow-auto">
      <div className="flex flex-row h-9 items-center justify-between">
        <span className="text-sm font-semibold">
          {filters.date
            ? `${format(filters.date, "PPP")} - ${format(filters.date, "EEEE")}`
            : filters.client
              ? `Showing all tasks for ${getFirstName(filters.client?.name)}`
              : isFamilyMode
                ? "Showing all family tasks"
                : "Showing all tasks"}
        </span>
        <Button
          className={cn(isAddingTask && "invisible")}
          onClick={() => setIsAddingTask(true)}
          variant="ghost"
        >
          <PlusIcon />
          Add task
        </Button>
      </div>
      <ConditionalRenderer condition={isAddingTask}>
        <div className="mb-4">
          <TaskEditor onFinish={() => setIsAddingTask(false)} />
        </div>
      </ConditionalRenderer>
      <div className="flex flex-col flex-1 gap-6">
        {tasks?.map((t) => (
          <NoteTaskItem
            key={t.id}
            task={t}
            isEditing={editingTaskId === t.id}
            onEditStart={() => {
              setIsAddingTask(false);
              setEditingTaskId(t.id);
            }}
            subText={
              <div className="flex flex-row gap-3 items-center">
                <span className="text-xs font-semibold text-muted-foreground">
                  {t.client_name ?? "Self"}
                </span>
                <TaskNoteBtn task={t} />
              </div>
            }
            onEditEnd={() => setEditingTaskId(null)}
          />
        ))}
        {tasks?.length === 0 && (
          <div className="flex flex-col flex-1 items-center gap-4 justify-center">
            <CalendarIcon className="size-12 stroke-1 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              No tasks on this day
            </span>
          </div>
        )}
      </div>

      <ConditionalRenderer condition={!!pendingTasks?.length}>
        <div className="h-px shrink-0 w-full bg-border my-2" />
        <div className="flex flex-row h-9 items-center justify-between">
          <span className="text-sm text-tag-yellow-foreground font-semibold">
            {pendingTasks?.length} Pending tasks
          </span>
        </div>

        <div className="flex flex-col flex-1 gap-6">
          {pendingTasks?.map((t) => (
            <NoteTaskItem
              key={t.id}
              task={t}
              isEditing={editingTaskId === t.id}
              onEditStart={() => {
                setIsAddingTask(false);
                setEditingTaskId(t.id);
              }}
              subText={
                <div className="flex flex-row gap-3 items-center">
                  <span className="text-xs font-semibold text-muted-foreground">
                    {t.client_name ?? "Self"}
                  </span>
                  {t.date ? (
                    <div className="flex text-xs font-semibold items-center gap-1 text-tag-yellow-foreground">
                      <HistoryIcon className="size-3" />
                      {format(t.date, "PPP")}
                    </div>
                  ) : null}
                  <TaskNoteBtn task={t} />
                </div>
              }
              onEditEnd={() => setEditingTaskId(null)}
            />
          ))}
          {pendingTasks?.length === 0 && (
            <div className="flex flex-col flex-1 items-center gap-4 justify-center">
              <CalendarIcon className="size-12 stroke-1 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                No tasks on this day
              </span>
            </div>
          )}
        </div>
      </ConditionalRenderer>
    </div>
  );
}

function TaskNoteBtn({ task }: { task: NoteTask }) {
  const fetchNote = useFetchNote();

  const { setView, setSelectedNote } = useNotebookContext();

  if (!task.note_id) return null;

  return (
    <button
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        fetchNote
          .mutateAsync(task.note_id as number)
          .then((n) => {
            setView("notes");
            setSelectedNote(transformNoteDetailToListItem(n));
          })
          .catch((e) => handleError(e, "Unable to fetch note"));
      }}
      className="p-1 rounded group/note bg-secondary hover:bg-secondary/80 active:translate-y-px"
    >
      <StickyNoteIcon className="size-3 text-muted-foreground group-hover/note:text-foreground" />
    </button>
  );
}
